import { memo, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import ResizableMarkdownImage from './ResizableMarkdownImage';
import MarkdownPythonCodeBlock from './MarkdownPythonCodeBlock';
import {
  selectionIntersectsNode,
  serializeSelectedTableAsSpaces,
} from '../utils/tableCopyAsSpaces';
import './MarkdownText.css';

/** Схема санитайза: math-* на code для rehype-katex; sup/sub и типичный HTML из условий после rehype-raw. Порядок: rehypeRaw → sanitize → katex. */
const markdownSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    table: [
      ...(defaultSchema.attributes?.table ?? []),
      'style',
    ],
    colgroup: [
      ...(defaultSchema.attributes?.colgroup ?? []),
      'style',
    ],
    col: [
      ...(defaultSchema.attributes?.col ?? []),
      'style',
    ],
    tr: [
      ...(defaultSchema.attributes?.tr ?? []),
      'style',
    ],
    th: [
      ...(defaultSchema.attributes?.th ?? []),
      'style',
    ],
    td: [
      ...(defaultSchema.attributes?.td ?? []),
      'style',
    ],
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ['className', 'md-neq'],
    ],
    code: [
      ['className', /^language-./],
      ['className', 'math-inline'],
      ['className', 'math-display'],
    ],
  },
};

/**
 * В GFM символ * — разделитель курсива. В масках вида «43*6*» звёздочки исчезают.
 * Экранируем * внутри «…», если там маска (цифры/?) или только звёздочки — т.к. «*» в
 * определении маски (ЕГЭ-25) не содержит цифр, старый критерий пропускал такие случаи.
 */
function escapeAsterisksInGuillemetMasks(src) {
  return src.replace(/«([^»]*)»/g, (full, inner) => {
    if (!inner.includes('*')) return full;
    const t = inner.trim();
    const maskLike = /[0-9?]/.test(inner) || /^\*+$/.test(t);
    if (!maskLike) return full;
    return `«${inner.replace(/\*/g, '\\*')}»`;
  });
}

/** «Утерянный» разряд / маска: подряд * сразу перед <sub> (например ***₁₆, 1**₈). */
function escapeAsteriskRunsBeforeSub(src) {
  return src.replace(/(?<!\\)\*+(?=<sub\b)/g, (m) => m.replace(/\*/g, '\\*'));
}

/** *E₁₆, *A₁₆ — одна или несколько * перед цифрой/буквой, за которой сразу <sub>. */
function escapeAsteriskRunsBeforeCharThenSub(src) {
  return src.replace(/(?<!\\)\*+(?=[A-Za-zА-Яа-яЁё0-9]<sub\b)/g, (m) =>
    m.replace(/\*/g, '\\*'),
  );
}

/**
 * В записях СС с <sub> звёздочка между цифрами — почти всегда «потерянный» разряд, не умножение.
 * Ограничиваем контекстом наличия <sub> в тексте (как у задач 14 и др.).
 */
function escapeAsterisksBetweenDigitsInRadixContext(src) {
  if (!src.includes('<sub>')) return src;
  return src.replace(/(?<!\\)(\d)(\*+)(\d)/g, (_m, a, stars, b) => a + stars.replace(/\*/g, '\\*') + b);
}

/** «Позиции … обозначены *:» — одиночная * перед двоеточием. */
function escapeAsteriskRunsBeforeColon(src) {
  return src.replace(/(?<!\\)\*+(?=:)/g, (m) => m.replace(/\*/g, '\\*'));
}

/**
 * Звёздочка как умножение (GFM иначе даёт курсив): 2,2*F → 2,2·F; 10*F → 10·F.
 * Не трогаем * в масках вроде 1*5 (цифра после *) и ** (уже обработано выше).
 */
function replaceMultiplicationAsterisks(src) {
  let s = src;
  // 2**5 в записях вида «= 2**5<sub>8</sub>» — не смысл markdown **…**
  s = s.replace(/(\d+)\*\*(\d+)/g, '$1\\*\\*$2');
  // Десятичный коэффициент × функция/скобка: 2,2*F, 1.7*F
  s = s.replace(
    /(\d+[.,]\d+)\*(?!\*)(?=[A-Za-zА-Яа-яЁё(])/g,
    '$1·',
  );
  // Целый коэффициент × функция/скобка: 10*F(…); не 1*5 (маска IP), не 2**5
  s = s.replace(
    /(\d+)\*(?!\*)(?=[A-Za-zА-Яа-яЁё(])/g,
    '$1·',
  );
  // Утерянный разряд: 18*<sub>16</sub> — иначе * читается как курсив GFM
  s = s.replace(/(\d+)\*(\s*)(?=<)/g, '$1\\*$2');
  // *D*<sub>, *A*<sub> — потерянный символ в записи числа, не пара курсива
  s = s.replace(
    /\*([A-Za-zА-Яа-яЁё0-9])\*(?=<)/g,
    '\\*$1\\*',
  );
  return s;
}

/**
 * Голые маски ЕГЭ-25 без кавычек: «маске 12*45*» — GFM иначе включает курсив.
 * Токен из цифр/?/* с хотя бы одной цифрой или ?: экранируем все *.
 * Не трогает *курсив*, **жирный**, списки «* пункт».
 */
function escapeBareNumericMaskAsterisks(src) {
  return src.replace(/[0-9?*]+/g, (m) => {
    if (!m.includes('*') || !/[0-9?]/.test(m)) return m;
    return m.replace(/\*/g, '\\*');
  });
}

/** Частый в условиях ЕГЭ вид без LaTeX-разметки: «2·10^9» → inline-формула для KaTeX */
function preprocessExponentAfterMiddleDot(src) {
  return src.replace(
    /(\d+)\s*[·⋅]\s*10\^(\d+)/g,
    (_m, a, b) => `$${a}\\cdot 10^{${b}}$`,
  );
}

/** Путь из [IMG:ege15.files/image001.gif] — только относительный файл картинки. */
const SAFE_IMG_PLACEHOLDER_PATH =
  /^[A-Za-z0-9][A-Za-z0-9._/-]*\.(?:gif|png|jpe?g|webp|bmp)$/i;

/**
 * Импорт с сайта Полякова оставил плейсхолдеры вместо <img>.
 * Часто они ещё обёрнуты в <sub> (артефакт Word HTML) — вынимаем, иначе формула будет мелкой.
 */
export function replaceImgPlaceholders(src) {
  const toImg = (rawPath, original) => {
    const path = String(rawPath || '').trim().replace(/\\/g, '/');
    if (path.includes('..') || !SAFE_IMG_PLACEHOLDER_PATH.test(path)) {
      return original;
    }
    return `<img src="/media/${path}" alt="" />`;
  };
  let out = src.replace(
    /<(sub|sup)\s*>\s*\[IMG:([^\]]+?)\]\s*<\/\1>/gi,
    (m, _tag, p) => toImg(p, m),
  );
  out = out.replace(/\[IMG:([^\]]+?)\]/g, (m, p) => toImg(p, m));
  return out;
}

/**
 * Препроцессоры для масок ЕГЭ не должны трогать fenced/inline code
 * (иначе `10**9` превращается в `10\*\*9`).
 */
function mapOutsideCode(src, fn) {
  const re = /```[\s\S]*?```|`[^`\n]*`/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    out += fn(src.slice(last, m.index));
    out += m[0];
    last = m.index + m[0].length;
  }
  out += fn(src.slice(last));
  return out;
}

/** Не трогать уже готовые $...$ / $$...$$ (иначе сломаем вложенный KaTeX). */
function mapOutsideDollarMath(src, fn) {
  const re = /\$\$[\s\S]*?\$\$|\$(?:\\.|[^$\n])+\$/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    out += fn(src.slice(last, m.index));
    out += m[0];
    last = m.index + m[0].length;
  }
  out += fn(src.slice(last));
  return out;
}

/**
 * ≠ (U+2260) в system-ui / Segoe UI часто рисуется как «=» с кривым слешем
 * (похоже на =/ или /=). JetBrains Mono уже подключён и даёт нормальный глиф;
 * копируется тот же ≠, не «=».
 */
function wrapBareNeqForFont(src) {
  return mapOutsideDollarMath(src, (chunk) =>
    chunk.replace(/≠|=\u0338/g, '<span class="md-neq">≠</span>'),
  );
}

function preprocessMarkdownForEge(src) {
  return mapOutsideCode(src, (chunk) => {
    let content = replaceImgPlaceholders(chunk);
    content = escapeAsterisksInGuillemetMasks(content);
    content = content.replace(/"([0-9?*]+)"/g, (full, inner) => {
      if (!inner.includes('*')) return full;
      return `"${inner.replace(/\*/g, '\\*')}"`;
    });
    content = escapeAsteriskRunsBeforeColon(content);
    content = escapeAsteriskRunsBeforeSub(content);
    content = escapeAsteriskRunsBeforeCharThenSub(content);
    content = escapeAsterisksBetweenDigitsInRadixContext(content);
    content = replaceMultiplicationAsterisks(content);
    content = escapeBareNumericMaskAsterisks(content);
    content = preprocessExponentAfterMiddleDot(content);
    content = wrapBareNeqForFont(content);
    return content;
  });
}

function MarkdownText({
  text,
  className = '',
  /** ЕГЭ-2: при копировании таблицы — ячейки через один пробел, не TAB. */
  copyTablesAsSpaces = false,
}) {
  const rootRef = useRef(null);

  // copy часто уходит на document/body, а не через div с выделением —
  // слушаем document и проверяем, что выделение внутри нашего блока.
  useEffect(() => {
    if (!copyTablesAsSpaces) return undefined;

    const onCopy = (e) => {
      const root = rootRef.current;
      const sel = window.getSelection();
      if (!root || !sel || sel.isCollapsed) return;
      if (!selectionIntersectsNode(sel, root)) return;

      const plain = serializeSelectedTableAsSpaces(sel);
      if (plain == null) return;

      e.preventDefault();
      e.clipboardData?.setData('text/plain', plain);
    };

    document.addEventListener('copy', onCopy);
    return () => document.removeEventListener('copy', onCopy);
  }, [copyTablesAsSpaces]);

  let content = String(text ?? '').trim();
  if (!content) {
    return null;
  }
  content = preprocessMarkdownForEge(content);

  return (
    <div
      ref={rootRef}
      className={`markdown-text ${className}`.trim()}
    >
      <ReactMarkdown
        components={{
          img: ResizableMarkdownImage,
          code({ className, children, ...props }) {
            const text = String(children).replace(/\n$/, '');
            if (/language-python/.test(className || '')) {
              return <MarkdownPythonCodeBlock code={text} />;
            }
            if (!className) {
              return (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }
            return (
              <pre className="markdown-code-block-fallback">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            );
          },
        }}
        remarkPlugins={[
          remarkGfm,
          remarkMath,
          // Одиночные \n в условиях ЕГЭ (и др.) — не схлопывать в пробел (CommonMark).
          remarkBreaks,
        ]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, markdownSanitizeSchema],
          rehypeKatex,
        ]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default memo(MarkdownText);
